import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import { Radar } from 'react-chartjs-2';

import {
  getHistoryOfOrderingDetail,
  getScalpProducts,
  getScalpAdvice,
  getScalpMetricsAdvice,
  checkNiitaContract,
} from '../../../../services/api/1C';
import DefaultLayout from '../../../../layouts/DefaultLayout';
import MainContentLayout from '../../../../layouts/MainContentLayout';
import LoadingProgress from '../../../../components/commonUI/LoadingProgress';
import LoadInitError from '../../../../components/commonUI/LoadInitError';
import NoData from '../../../../components/commonUI/NoData';
import InvalidClusterResult from './InvalidClusterResult';
import { SelectItem } from './SelectItem';
import useDimension from '../../../../hooks/useDimension';
import UserPoint, { turnoverImages, oxidationImages, drynessImages } from './UserPoint';
import UserPointItem from './UserPoint/Item';
import { useSelector } from 'react-redux';
import Path from 'app/route/Path';
import {
  NiitaHeader,
  NiitaInterpretationBlock,
  NiitaRecommendationBlock,
  NiitaPersonalizationBlock,
} from './NiitaAdvice';
import NiitaConsultation from './NiitaConsultation';
import { toast } from 'react-toastify';
import TypesReferenceInfo from './TypesReferenceInfo';
import { ANALYSE_STATUS } from '../../../../constants';
import { convertClusterToClass } from './helpers';
import {
  AVERAGE_SCORE_BY_CLUSTER,
  BACKGROUND_COLOR_BY_CLUSTER,
  BORDER_COLOR_BY_CLUSTER,
  DEFAULT_RADAR_DATA,
  EXPECTED_HEIGHT,
  EXPECTED_RATIO,
  EVALUATION_BACKGROUND_COLOR_BY_CLUSTER,
  TITLE_COLOR_BY_CLUSTER,
  EVALUATION_BORDER_COLOR_BY_CLUSTER,
  SECTION_TITLE_COLOR_BY_CLUSTER,
  CLUSTER,
} from './constants';
import moment from 'moment';
import { getInventory } from '../../../../services/api/18';
// Plugin radar
const radarValueLabelsPlugin = {
  id: 'radarValueLabels',
  afterDatasetsDraw: (chart) => {
    const { ctx } = chart;
    const dataset = chart.data.datasets[1];
    if (!dataset) return;
    if (dataset.label !== '# of Votes') return;
    const meta = chart.getDatasetMeta(1);
    if (!meta || !meta.data) return;

    ctx.save();
    ctx.font = 'bold 12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    meta.data.forEach((point, index) => {
      const value = dataset.data[index];
      const { x, y } = point;
      const centerX = chart.scales.r.xCenter;
      const centerY = chart.scales.r.yCenter;
      const angle = Math.atan2(y - centerY, x - centerX);
      const offsetDistance = 4;

      const labelX = x + Math.cos(angle) * offsetDistance;
      const labelY = y + Math.sin(angle) * offsetDistance;

      const textWidth = ctx.measureText(value).width;
      const paddingX = 8;
      const height = 16;
      const width = textWidth + paddingX * 2;
      const radius = height / 2;

      ctx.beginPath();
      ctx.moveTo(labelX - width / 2 + radius, labelY - height / 2);
      ctx.lineTo(labelX + width / 2 - radius, labelY - height / 2);
      ctx.arcTo(labelX + width / 2, labelY - height / 2, labelX + width / 2, labelY, radius);
      ctx.arcTo(labelX + width / 2, labelY + height / 2, labelX + width / 2 - radius, labelY + height / 2, radius);
      ctx.lineTo(labelX - width / 2 + radius, labelY + height / 2);
      ctx.arcTo(labelX - width / 2, labelY + height / 2, labelX - width / 2, labelY, radius);
      ctx.arcTo(labelX - width / 2, labelY - height / 2, labelX - width / 2 + radius, labelY - height / 2, radius);
      ctx.closePath();

      // Fill background
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fill();

      // Text
      ctx.fillStyle = '#0EA5E9';
      ctx.fillText(value, labelX, labelY);
    });

    ctx.restore();
  },
};

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend, radarValueLabelsPlugin);

const NAV_ITEMS = [
  { href: '#result', label: '結果' },
  { href: '#metrics', label: '指標詳細' },
  { href: '#ai-advice', label: '提案' },
  { href: '#products', label: '商品' },
];

function HistoryOfOrderDetailByKitIdPage() {
  const customerNo = useParams();
  const scroll = React.useRef(null);
  const radarRef = React.useRef();
  const user = useSelector((state) => state.auth?.user);

  const [fetchHistoryOrderDetail, setFetchHistoryOrderDetail] = useState({
    isLoading: false,
    data: null,
    error: null,
  });
  const [radarData, setRadarData] = useState(DEFAULT_RADAR_DATA);
  const [scalpData, setScalpData] = useState(false);
  const [products, setProducts] = useState([]);
  const [advice, setAdvice] = useState(null);
  const [metricsAdvice, setMetricsAdvice] = useState(null);
  const [hasNiitaContract, setHasNiitaContract] = useState(false);

  const navigate = useNavigate();

  const isFactorClusterInvalid = scalpData.factorCluster === 0;

  const { width } = useDimension();
  const isTablet = width > 768;

  const labelSize = 14;
  const chartHeight = (width > EXPECTED_HEIGHT ? EXPECTED_HEIGHT : width) / EXPECTED_RATIO;
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
      },
      tooltip: {
        filter: (item) => item.datasetIndex !== 2,
      },
    },
    scales: {
      r: {
        angleLines: {
          display: true,
          color: 'rgba(200, 200, 200, 0.5)',
          lineWidth: 1,
        },
        min: 0,
        max: 10,
        beginAtZero: true,
        padding: 0,
        backgroundColor: '#F3F3F3',
        grid: {
          color: function (context) {
            if (context.tick && context.tick.value === 10) {
              return 'rgba(200, 200, 200, 0.6)';
            }
            return 'transparent';
          },
          lineWidth: 1,
        },
        ticks: {
          display: false,
          showLabelBackdrop: false,
          stepSize: 2,
        },
        pointLabels: {
          display: true,
          padding: 6,
          color: '#64748b',
          font: {
            size: labelSize,
            weight: 'bold',
          },
        },
      },
    },
  };

  const loadHistoryOrderDetail = () => {
    setFetchHistoryOrderDetail({ isLoading: true, data: null, error: null });
    getHistoryOfOrderingDetail(customerNo.customerNo)
      .then((res) => {
        const found = res.data.find(function (item) {
          return item.scalpData.inspectionKitId === customerNo.inspectionKitId;
        });
        setFetchHistoryOrderDetail({ isLoading: false, data: found, error: null });
      })
      .catch((error) => {
        setFetchHistoryOrderDetail({ isLoading: false, data: null, error: error });
        toast.error('データの取得に失敗しました');
      });
  };

  const loadScalpProducts = async () => {
    try {
      const res = await getScalpProducts(scalpData?.factorCluster);
      let _products = Array.isArray(res) ? res : (res?.result ?? []);
      if (hasNiitaContract && advice.pickupSelectItemIds) {
        // Convert to Set for O(1) lookup
        const pickupSet = new Set(advice.pickupSelectItemIds);
        _products = _products.map((eachProduct) => ({
          ...eachProduct,
          isNiitaRecommended: pickupSet.has(eachProduct._id),
        }));
      }

      setProducts(_products);
    } catch {
      toast.error('商品情報の取得に失敗しました');
    }
  };

  const scrollToBottom = () => {
    scroll.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const displayName = useMemo(() => {
    const {
      lastNameKanji = '',
      firstNameKanji = '',
      lastNameKata = '',
      firstNameKata = '',
    } = fetchHistoryOrderDetail?.data?.customer || {};
    const lastName = lastNameKanji || lastNameKata || '';
    const firstName = firstNameKanji || firstNameKata || '';
    return `${lastName} ${firstName}さん`;
  }, [fetchHistoryOrderDetail]);

  useEffect(() => {
    if (radarRef.current) {
      radarRef.current.scales.r.options.pointLabels._proxy.font.size = labelSize;
      radarRef.current.canvas.style.height = chartHeight + 'px';
    }
  }, [chartHeight, labelSize]);

  useEffect(() => {
    loadHistoryOrderDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMetricsAdvice = async () => {
    try {
      const res = await getScalpMetricsAdvice(customerNo.inspectionKitId);
      setMetricsAdvice(res?.data ?? null);
    } catch {
      toast.error('指標アドバイスの取得に失敗しました');
    }
  };

  const loadNiitaContract = async () => {
    try {
      const res = await checkNiitaContract();
      setHasNiitaContract(res?.hasNiitaContract ?? false);
    } catch {
      toast.error('契約情報の取得に失敗しました');
    }
  };

  useEffect(() => {
    loadNiitaContract();
  }, []);
  const loadAdvice = async (personalization = {}, regen = false) => {
    try {
      const res = await getScalpAdvice(customerNo.inspectionKitId, personalization, regen);

      if (regen) {
        // If regenerate, also reload the MetricsAdvice
        await loadMetricsAdvice(customerNo.inspectionKitId);
        await loadScalpProducts();
      }

      setAdvice(res?.data?.result ?? null);
    } catch {
      toast.error('スカルプアドバイス情報の取得に失敗しました');
    }
  };

  useEffect(() => {
    if (scalpData?.factorCluster) {
      loadAdvice();
      loadMetricsAdvice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scalpData]);

  useEffect(() => {
    loadScalpProducts();
  }, [advice]);

  useEffect(() => {
    // 在这里执行你想要的操作，可以访问 fetchHistoryOrderDetail 的最新值
    // console.log(
    //   "fetchHistoryOrderDetail changed:",
    //   fetchHistoryOrderDetail
    // );

    // 例如，检查 isLoading 是否为 true，然后执行相应的操作
    if (fetchHistoryOrderDetail.isLoading) {
      // 执行你的操作...
    } else {
      if (fetchHistoryOrderDetail.data?.scalpData?.factorCellArea == null) {
      } else if (fetchHistoryOrderDetail.data?.scalpData?.factorPeeling == null) {
      } else if (fetchHistoryOrderDetail.data?.scalpData?.factorCP == null) {
      } else {
        const cluster = fetchHistoryOrderDetail.data?.scalpData?.factorCluster;

        const backgroundColor = BACKGROUND_COLOR_BY_CLUSTER[cluster] ?? 'rgba(157, 193, 226, 0.7)';
        const borderColor = BORDER_COLOR_BY_CLUSTER[cluster] ?? '';
        const averageValues = AVERAGE_SCORE_BY_CLUSTER[cluster] ?? [];
        const convertedTScores = [averageValues[0] ?? 0, averageValues[2] ?? 0, averageValues[1] ?? 0];
        const bgSummaryColor = EVALUATION_BACKGROUND_COLOR_BY_CLUSTER[cluster] ?? 'rgba(157, 193, 226, 0.7)';
        const summaryColor = TITLE_COLOR_BY_CLUSTER[cluster] ?? '#64748B';
        const borderSummaryColor = EVALUATION_BORDER_COLOR_BY_CLUSTER[cluster] ?? 'rgba(157, 193, 226, 0.7)';
        const factorCellArea = (
          Math.round(fetchHistoryOrderDetail.data?.scalpData?.factorCellArea * 1000) / 1000
        ).toFixed(3);

        const factorPeeling = (
          Math.round(fetchHistoryOrderDetail.data?.scalpData?.factorPeeling * 1000) / 1000
        ).toFixed(3);

        const factorCP = (Math.round(fetchHistoryOrderDetail.data?.scalpData?.factorCP * 1000) / 1000).toFixed(3);

        const barCA = fetchHistoryOrderDetail.data?.scalpData?.barCA ?? 0;

        const barCP = fetchHistoryOrderDetail.data?.scalpData?.barCP ?? 0;

        const barMP = fetchHistoryOrderDetail.data?.scalpData?.barMP ?? 0;

        const ratioCA = fetchHistoryOrderDetail.data?.scalpData?.ratioCA ?? 0;

        const ratioCP = fetchHistoryOrderDetail.data?.scalpData?.ratioCP ?? 0;

        const ratioMP = fetchHistoryOrderDetail.data?.scalpData?.ratioMP ?? 0;

        setRadarData({
          labels: ['TO', 'OX', 'DR'],
          datasets: [
            {
              label: ' average value',
              data: convertedTScores,

              order: 2,
              borderColor,
              backgroundColor: 'transparent',
              borderWidth: 4,
              pointStyle: false,
            },
            {
              label: '# of Votes',
              data: [
                (Math.round(factorCellArea * 10) / 10).toFixed(1),
                (Math.round(factorCP * 10) / 10).toFixed(1),
                (Math.round(factorPeeling * 10) / 10).toFixed(1),
              ],
              order: 1,
              backgroundColor,
              borderColor: backgroundColor,
              borderWidth: 1,
              pointStyle: false,
            },
            {
              label: 'middle line',
              data: [5, 5, 5],
              order: 3,
              borderColor: '#e5e7eb',
              backgroundColor: 'transparent',
              borderWidth: 2,
              borderDash: [5, 5],
              pointStyle: false,
            },
          ],
        });
        const getValueColor = (point, avg) => {
          const userPercent = ((10 - point) / 10) * 100;
          const avgPercent = ((10 - avg) / 10) * 100;
          if (userPercent < avgPercent - 10) return '#0EA5E9';
          if (userPercent > avgPercent + 10) return '#EF4444';
          return '#FACC15';
        };

        const metricsColors = {
          'TO-01': getValueColor(factorCellArea, averageValues[0] ?? 0),
          'OX-02': getValueColor(factorCP, averageValues[2] ?? 0),
          'DR-03': getValueColor(factorPeeling, averageValues[1] ?? 0),
        };
        setScalpData({
          factorCellArea: factorCellArea,
          factorPeeling: factorPeeling,
          factorCP: factorCP,
          factorScore: Math.round(fetchHistoryOrderDetail.data?.scalpData?.factorScore),
          factorCluster: Math.round(fetchHistoryOrderDetail.data?.scalpData?.factorCluster),
          barCA,
          barMP,
          barCP,
          ratioCA,
          ratioMP,
          ratioCP,
          bgSummaryColor,
          summaryColor,
          borderSummaryColor,
          metricsColors,
        });
      }

      if (
        fetchHistoryOrderDetail.data?.scalpData?.analyseStatus === ANALYSE_STATUS.ANALYSE_NOT_STARTED ||
        fetchHistoryOrderDetail.data?.scalpData?.analyseStatus === ANALYSE_STATUS.ANALYSE_IN_PROGRESS
      ) {
        setScalpData({});
      }
    }
  }, [fetchHistoryOrderDetail]);

  return (
    <DefaultLayout
      {...(!isFactorClusterInvalid && !isTablet && { headerProps: { navItems: NAV_ITEMS, activeNavItem: '' } })}
    >
      <MainContentLayout
        pageTitle={
          <>
            <p>ラボ</p>
          </>
        }
      >
        <section className="bg-white rounded-xl p-4 shadow-md border border-slate-200  mx-4 mb-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Report ID</div>
              <div className="text-sm font-mono font-bold text-slate-700">{customerNo.inspectionKitId}</div>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="text-xs border border-slate-300 rounded px-3 py-1 text-slate-600 hover:bg-slate-50 transition"
            >
              他の検体を見る
            </button>
          </div>
          <div className="grid grid-cols-2 gap-y-2 gap-x-4 pt-3 border-t border-slate-100">
            <div className="col-span-2">
              <div className="text-[10px] text-slate-400">計測店舗名</div>
              <div className="text-xs font-medium text-slate-700">{user?.salonName || '-'}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400">お客様No.</div>
              <div className="text-xs font-medium text-slate-700">{customerNo.customerNo}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400">測定日</div>
              <div className="text-xs font-medium text-slate-700">
                {fetchHistoryOrderDetail.data?.scalpData?.createdAt
                  ? moment(fetchHistoryOrderDetail.data?.scalpData?.createdAt).format('YYYY-MM-DD HH:mm')
                  : '-'}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400">測定種別</div>
              <div className="text-xs font-medium text-slate-700">角層解析 (Stratum)</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-400">レポート種別</div>
              <div className="text-xs font-medium text-slate-700">Scalp Assessment v2.0</div>
            </div>
          </div>
        </section>

        {isTablet ? (
          // Is Tablet
          <div className="w-full h-full mx-auto relative max-h-full flex flex-wrap pt-8 pb-8">
            <div className="w-full h-full py-3 px-2">
              {fetchHistoryOrderDetail.isLoading && <LoadingProgress />}
              {!fetchHistoryOrderDetail.isLoading && fetchHistoryOrderDetail.error ? (
                <LoadInitError error={fetchHistoryOrderDetail.error} />
              ) : null}

              {!fetchHistoryOrderDetail.isLoading && !fetchHistoryOrderDetail.error ? (
                <div className="md:flex gap-6 h-full">
                  <div className="w-full flex flex-col h-1/2 md:h-full gap-6 items-center">
                    {!scalpData ? (
                      <LoadingProgress />
                    ) : (
                      <>
                        {isFactorClusterInvalid ? (
                          <InvalidClusterResult hasNiitaContract={hasNiitaContract} />
                        ) : (
                          // Main Content (Tablet)
                          <div className="w-full flex border-b pb-8">
                            <div className="w-full">
                              {scalpData?.factorCluster && hasNiitaContract ? (
                                (() => {
                                  const sectionTitleColor =
                                    SECTION_TITLE_COLOR_BY_CLUSTER[scalpData?.factorCluster] ?? '#64748B';
                                  const isClusterE = [CLUSTER.E_CLASS, CLUSTER.BD_CLASS, CLUSTER.ED_CLASS].includes(
                                    scalpData?.factorCluster?.toString(),
                                  );
                                  const itemBgColor = isClusterE
                                    ? EVALUATION_BACKGROUND_COLOR_BY_CLUSTER[CLUSTER.E_CLASS]
                                    : undefined;
                                  const itemBorderColor = isClusterE
                                    ? EVALUATION_BORDER_COLOR_BY_CLUSTER[CLUSTER.E_CLASS]
                                    : undefined;
                                  const interpretationColor = isClusterE
                                    ? SECTION_TITLE_COLOR_BY_CLUSTER[CLUSTER.E_CLASS]
                                    : undefined;
                                  const averages = AVERAGE_SCORE_BY_CLUSTER[scalpData?.factorCluster] ?? [0, 0, 0];
                                  const hasInterpretation = advice?.items?.length > 0;
                                  const hasRecommendations = advice?.recommendations?.length > 0;

                                  const to01Item = (
                                    <UserPointItem
                                      code="TO-01"
                                      label="細胞面積"
                                      labelEn="Turnover"
                                      point={scalpData?.factorCellArea}
                                      ratio={scalpData?.ratioCA}
                                      avg={averages[0]}
                                      isTablet={isTablet}
                                      microscopeImages={turnoverImages}
                                      interpretation={metricsAdvice?.['TO-01']?.interpretation}
                                      recommendation={metricsAdvice?.['TO-01']?.recommendation}
                                      sectionTitleColor={sectionTitleColor}
                                      bgColor={itemBgColor}
                                      borderColor={itemBorderColor}
                                      interpretationColor={interpretationColor}
                                    />
                                  );
                                  const ox02Item = (
                                    <UserPointItem
                                      code="OX-02"
                                      label="カルボニルタンパク"
                                      labelEn="Oxidation"
                                      point={scalpData?.factorCP}
                                      ratio={scalpData?.ratioCP}
                                      avg={averages[2]}
                                      isTablet={isTablet}
                                      microscopeImages={oxidationImages}
                                      interpretation={metricsAdvice?.['OX-02']?.interpretation}
                                      recommendation={metricsAdvice?.['OX-02']?.recommendation}
                                      sectionTitleColor={sectionTitleColor}
                                      bgColor={itemBgColor}
                                      borderColor={itemBorderColor}
                                      interpretationColor={interpretationColor}
                                    />
                                  );
                                  const dr03Item = (
                                    <UserPointItem
                                      code="DR-03"
                                      label="多重剥離度"
                                      labelEn="Dryness"
                                      point={scalpData?.factorPeeling}
                                      ratio={scalpData?.ratioMP}
                                      avg={averages[1]}
                                      isTablet={isTablet}
                                      microscopeImages={drynessImages}
                                      interpretation={metricsAdvice?.['DR-03']?.interpretation}
                                      recommendation={metricsAdvice?.['DR-03']?.recommendation}
                                      sectionTitleColor={sectionTitleColor}
                                      bgColor={itemBgColor}
                                      borderColor={itemBorderColor}
                                      interpretationColor={interpretationColor}
                                    />
                                  );
                                  const scalpProfileSection = (
                                    <section className="bg-white rounded-xl p-5 shadow-medical border border-slate-200 relative">
                                      <div className="flex justify-between items-start mb-4">
                                        <div>
                                          <h3 className="text-sm font-bold text-slate-800">
                                            Scalp Profile{' '}
                                            <span className="text-xs font-normal text-slate-500">（分析チャート）</span>
                                          </h3>
                                          <p className="text-[10px] text-slate-400 mt-1">3指標のバランス分析</p>
                                        </div>
                                        <span className="material-symbols-outlined text-slate-300 text-lg">info</span>
                                      </div>
                                      <div className="w-full h-auto mb-[-30px] md:mb-[-40px]">
                                        <Radar
                                          ref={radarRef}
                                          onClick={scrollToBottom}
                                          data={radarData ? radarData : {}}
                                          options={chartOptions}
                                          height={chartHeight}
                                          plugins={[radarValueLabelsPlugin]}
                                        />
                                      </div>
                                      {advice?.radarSummary && (
                                        <div
                                          className="bg-sky-50 rounded border border-sky-100 p-2 text-center"
                                          style={{
                                            backgroundColor: scalpData?.bgSummaryColor,
                                            borderColor: scalpData?.borderSummaryColor,
                                          }}
                                        >
                                          <p
                                            className="text-xs text-slate-700 font-medium text-left"
                                            style={{ color: scalpData?.summaryColor }}
                                          >
                                            要約：{advice.radarSummary}
                                          </p>
                                        </div>
                                      )}
                                    </section>
                                  );

                                  const orphanedRightItems = [
                                    ...(!hasInterpretation ? [ox02Item] : []),
                                    ...(!hasRecommendations ? [dr03Item] : []),
                                  ];

                                  return (
                                    <div className="w-full flex flex-col gap-4">
                                      <div className="flex gap-4 items-start">
                                        <div className="flex-1">
                                          <TypesReferenceInfo
                                            type={convertClusterToClass(scalpData?.factorCluster)}
                                            commentTop={fetchHistoryOrderDetail.data.scalpData.commentTop}
                                            factorCluster={scalpData?.factorCluster}
                                            displayName={displayName}
                                            isTablet={isTablet}
                                          />
                                        </div>
                                        <div className="flex-1">{to01Item}</div>
                                      </div>

                                      {hasInterpretation && (
                                        <div className="flex gap-4 items-start">
                                          <div className="flex-1">
                                            <NiitaHeader />
                                            <NiitaInterpretationBlock
                                              advice={advice}
                                              metricsColors={scalpData?.metricsColors}
                                            />
                                          </div>
                                          <div className="flex-1 md:mt-9">{ox02Item}</div>
                                        </div>
                                      )}

                                      {hasRecommendations && (
                                        <div className="flex gap-4 items-start">
                                          <div className="flex-1">
                                            <NiitaRecommendationBlock recommendations={advice.recommendations} />
                                          </div>
                                          <div className="flex-1">{dr03Item}</div>
                                        </div>
                                      )}

                                      <div className="flex gap-4 items-start">
                                        <div className="flex-1">
                                          {!hasInterpretation && <NiitaHeader />}
                                          <NiitaPersonalizationBlock
                                            inHairSize={fetchHistoryOrderDetail.data.scalpData.hairSize}
                                            inGender={fetchHistoryOrderDetail.data.scalpData.gender}
                                            inAge={fetchHistoryOrderDetail.data.scalpData.age}
                                            onUpdate={async (params) => {
                                              const regen = true;
                                              await loadAdvice(params, regen);
                                            }}
                                          />
                                        </div>
                                        <div className="flex-1 flex flex-col gap-4">
                                          {orphanedRightItems.map((item, i) => (
                                            <div key={i}>{item}</div>
                                          ))}
                                          {scalpProfileSection}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()
                              ) : (
                                <div className="w-full">
                                  {scalpData?.factorCluster && (
                                    <div className="mb-6">
                                      <TypesReferenceInfo
                                        type={convertClusterToClass(scalpData?.factorCluster)}
                                        commentTop={fetchHistoryOrderDetail.data.scalpData.commentTop}
                                        factorCluster={scalpData?.factorCluster}
                                        displayName={displayName}
                                        isTablet={isTablet}
                                      />
                                    </div>
                                  )}
                                  <UserPoint scalpData={scalpData} metricsAdvice={metricsAdvice} isTablet />
                                  <section className="bg-white mt-6 rounded-xl p-5 shadow-medical border border-slate-200 relative">
                                    <div className="flex justify-between items-start mb-4">
                                      <div>
                                        <h3 className="text-sm font-bold text-slate-800">
                                          Scalp Profile{' '}
                                          <span className="text-xs font-normal text-slate-500">（分析チャート）</span>
                                        </h3>
                                        <p className="text-[10px] text-slate-400 mt-1">3指標のバランス分析</p>
                                      </div>
                                      <span className="material-symbols-outlined text-slate-300 text-lg">info</span>
                                    </div>
                                    <div className="w-full h-auto mb-[-30px] md:mb-[-40px]">
                                      <Radar
                                        ref={radarRef}
                                        onClick={scrollToBottom}
                                        data={radarData ? radarData : {}}
                                        options={chartOptions}
                                        height={chartHeight}
                                        plugins={[radarValueLabelsPlugin]}
                                      />
                                    </div>
                                    {advice?.radarSummary && (
                                      <div
                                        className="bg-sky-50 rounded border border-sky-100 p-2 text-center"
                                        style={{
                                          backgroundColor: scalpData?.bgSummaryColor,
                                          borderColor: scalpData?.borderSummaryColor,
                                        }}
                                      >
                                        <p
                                          className="text-xs text-slate-700 font-medium"
                                          style={{ color: scalpData?.summaryColor }}
                                        >
                                          要約：{advice.radarSummary}
                                        </p>
                                      </div>
                                    )}
                                  </section>
                                </div>
                              )}
                              {scalpData?.factorCluster && hasNiitaContract && (
                                <NiitaConsultation inspectionKitId={customerNo.inspectionKitId} />
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {!isFactorClusterInvalid && (
                      <SelectItem products={products} hasNiitaContract={hasNiitaContract} isTablet />
                    )}
                  </div>
                </div>
              ) : (
                <NoData />
              )}
            </div>
          </div>
        ) : (
          // Not Tablet
          <div className="w-full h-full mx-auto relative max-h-full flex flex-wrap pt-2 pb-8 lg:py-12">
            <div className="w-full h-full py-3 px-4">
              {fetchHistoryOrderDetail.isLoading && <LoadingProgress />}
              {!fetchHistoryOrderDetail.isLoading && fetchHistoryOrderDetail.error ? (
                <LoadInitError error={fetchHistoryOrderDetail.error} />
              ) : null}

              {!fetchHistoryOrderDetail.isLoading && !fetchHistoryOrderDetail.error ? (
                <div className="md:flex gap-6 h-full">
                  <div className="w-full flex flex-col h-1/2 md:h-full gap-6 items-center">
                    {!scalpData ? (
                      <LoadingProgress />
                    ) : (
                      <>
                        {isFactorClusterInvalid ? (
                          <InvalidClusterResult hasNiitaContract={hasNiitaContract} />
                        ) : (
                          // Main Content (Not Tablet)
                          <>
                            <div>
                              {scalpData?.factorCluster && (
                                <TypesReferenceInfo
                                  type={convertClusterToClass(scalpData?.factorCluster)}
                                  commentTop={fetchHistoryOrderDetail.data.scalpData.commentTop}
                                  factorCluster={scalpData?.factorCluster}
                                  displayName={displayName}
                                  isTablet={isTablet}
                                />
                              )}
                            </div>
                            <div className="w-full md:w-1/2 h-auto flex flex-col gap-6 items-start justify-center">
                              <UserPoint scalpData={scalpData} metricsAdvice={metricsAdvice} />

                              <section className="bg-white w-full rounded-xl p-5 shadow-medical border border-slate-200 relative">
                                <div className="flex justify-between items-start mb-4">
                                  <div>
                                    <h3 className="text-sm font-bold text-slate-800">
                                      Scalp Profile{' '}
                                      <span className="text-xs font-normal text-slate-500">（分析チャート）</span>
                                    </h3>
                                    <p className="text-[10px] text-slate-400 mt-1">3指標のバランス分析</p>
                                  </div>
                                  <span className="material-symbols-outlined text-slate-300 text-lg">info</span>
                                </div>
                                <div className="w-full h-auto mb-[-30px] md:mb-[-40px]">
                                  <Radar
                                    ref={radarRef}
                                    onClick={scrollToBottom}
                                    data={radarData ? radarData : {}}
                                    options={chartOptions}
                                    height={chartHeight}
                                    plugins={[radarValueLabelsPlugin]}
                                  />
                                </div>
                                {advice?.radarSummary && (
                                  <div
                                    className="bg-sky-50 rounded border border-sky-100 p-2 text-center"
                                    style={{
                                      backgroundColor: scalpData?.bgSummaryColor,
                                      borderColor: scalpData?.borderSummaryColor,
                                    }}
                                  >
                                    <p
                                      className="text-xs text-slate-700 font-medium"
                                      style={{ color: scalpData?.summaryColor }}
                                    >
                                      要約：{advice.radarSummary}
                                    </p>
                                  </div>
                                )}
                              </section>
                              {hasNiitaContract && (
                                <section className="w-full" id="ai-advice">
                                  <NiitaHeader />
                                  {advice?.items?.length > 0 && (
                                    <NiitaInterpretationBlock
                                      advice={advice}
                                      metricsColors={scalpData?.metricsColors}
                                    />
                                  )}
                                  {advice?.recommendations?.length > 0 && (
                                    <div className="mt-4">
                                      <NiitaRecommendationBlock recommendations={advice.recommendations} />
                                    </div>
                                  )}
                                  <div className="mt-4">
                                    <NiitaPersonalizationBlock
                                      inHairSize={fetchHistoryOrderDetail.data.scalpData.hairSize}
                                      inGender={fetchHistoryOrderDetail.data.scalpData.gender}
                                      inAge={fetchHistoryOrderDetail.data.scalpData.age}
                                      onUpdate={async (params) => {
                                        const regen = true;
                                        await loadAdvice(params, regen);
                                      }}
                                    />
                                  </div>
                                </section>
                              )}
                              {scalpData?.factorCluster && hasNiitaContract && (
                                <NiitaConsultation inspectionKitId={customerNo.inspectionKitId} />
                              )}
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {!isFactorClusterInvalid && (
                      <SelectItem products={products} hasNiitaContract={hasNiitaContract} isTablet />
                    )}
                  </div>
                </div>
              ) : (
                <NoData />
              )}
            </div>
          </div>
        )}
      </MainContentLayout>
    </DefaultLayout>
  );
}

export default HistoryOfOrderDetailByKitIdPage;
